import React, { useState, useEffect, useCallback } from "react";

// Helper to import all images from a folder
const importAll = (r) =>
  r.keys().map((key) => {
    const module = r(key);
    return module.default || module;
  });

// Define all gallery sections here
const gallerySections = [
  {
    title: "CRC Members Successfully Complete Mediation Training Conducted by IAMC Hyderabad",
    description:
      "The successful completion of the mediation training program and the certification ceremony received extensive coverage from National Media and Print Media, bringing wider recognition to the initiative of the Consumer Rights Council in collaboration with IAMC. The media highlighted the presence and contributions of Dr. Vikas Pandey, National President, Consumer Rights Council, Dr. Shanka Brata Bagchi, City Commissioner (DGP), Mr. Jawad AJ, Registrar, International Mediation and Arbitration Council (IAMC), Mr. Venkaramana, Additional District Judge, and Mrs. Tanuja Reddy, District Consumer Commission Judge. The coverage played a vital role in spreading awareness on the importance of mediation, alternative dispute resolution mechanisms, and consumer justice at both state and national levels.",
    images: importAll(
      require.context(
        "../../../assets/Aboutus/Press Highlight/2",
        false,
        /\.(png|jpe?g|webp)$/
      )
    ),
  },
  {
    title: "State Award from Government of Andhra Pradesh",
    description:
      "The Consumer Rights Council (CRC) is honoured to receive the State Award from the Government of Andhra Pradesh for its commitment to consumer protection and awareness. The award was conferred by senior government officials and received on behalf of CRC by Dr. Vikas Pandey, National President; Dr. Doddi Chandrashekhar, National Secretary; Kuncham Avinash Benjamin, National Spokesperson; and Mr. Vasudev Mahanthy, State Vice President, Andhra Pradesh. CRC sincerely thanks the National Media and Press for their valuable coverage and continued support in promoting consumer awareness.",
    images: importAll(
      require.context(
        "../../../assets/Aboutus/Press Highlight/1",
        false,
        /\.(png|jpe?g|webp)$/
      )
    ),
  },
  {
    title: "Other",
    images: importAll(
      require.context(
        "../../../assets/Aboutus/Press Highlight/Other",
        false,
        /\.(png|jpe?g|webp)$/
      )
    ),
  },
];

const Gallery = () => {
  // State stores the indices, not just the URL
  const [lightboxState, setLightboxState] = useState({
    isOpen: false,
    sectionIndex: 0,
    imageIndex: 0,
  });

  // Open specific image
  const openLightbox = (sIndex, imgIndex) => {
    setLightboxState({
      isOpen: true,
      sectionIndex: sIndex,
      imageIndex: imgIndex,
    });
  };

  // Close Modal
  const closeLightbox = () => {
    setLightboxState((prev) => ({ ...prev, isOpen: false }));
  };

  // Next Image Logic
  const showNext = useCallback((e) => {
    if (e) e.stopPropagation();
    setLightboxState((prev) => {
      const currentSectionImages = gallerySections[prev.sectionIndex].images;
      // Loop back to start if at the end
      const nextIndex =
        prev.imageIndex === currentSectionImages.length - 1
          ? 0
          : prev.imageIndex + 1;
      return { ...prev, imageIndex: nextIndex };
    });
  }, []);

  // Previous Image Logic
  const showPrev = useCallback((e) => {
    if (e) e.stopPropagation();
    setLightboxState((prev) => {
      const currentSectionImages = gallerySections[prev.sectionIndex].images;
      // Loop to end if at the start
      const prevIndex =
        prev.imageIndex === 0
          ? currentSectionImages.length - 1
          : prev.imageIndex - 1;
      return { ...prev, imageIndex: prevIndex };
    });
  }, []);

  // Keyboard Navigation (Left, Right, Escape)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxState.isOpen) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") showNext();
      if (e.key === "ArrowLeft") showPrev();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxState.isOpen, showNext, showPrev]);

  // Determine current image to display
  const currentImage = lightboxState.isOpen
    ? gallerySections[lightboxState.sectionIndex].images[
        lightboxState.imageIndex
      ]
    : "";

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-4 md:p-8 select-none">
      <div className="max-w-7xl mx-auto space-y-16 md:space-y-20">
        {gallerySections.map((section, sIndex) => (
          <div key={sIndex} className="group/section">
            {/* Section Header */}
            <div className="mb-8 md:mb-12 px-2">
              <div className="inline-flex items-center gap-3 mb-3"></div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
                {section.title}
              </h2>
              {section.description && (
                <p className="mt-4 text-lg text-gray-600 leading-relaxed">
                  {section.description}
                </p>
              )}
            </div>

            {/* Images Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {section.images.map((src, index) => (
                <div
                  key={index}
                  className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group/card transform hover:-translate-y-1"
                  onClick={() => openLightbox(sIndex, index)}
                >
                  {/* Image Container with Gradient Overlay */}
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={src}
                      alt={`${section.title}`}
                      className="w-full h-full object-cover transform transition-transform duration-500 group-hover/card:scale-110"
                      loading="lazy"
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center justify-between">
                          <span className="text-white text-sm font-medium px-3 py-1 bg-black/40 backdrop-blur-sm rounded-full">
                            View Details
                          </span>
                          <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <svg
                              className="w-5 h-5 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {lightboxState.isOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center animate-fadeIn"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            className="absolute top-6 right-6 md:top-8 md:right-8 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all duration-200 hover:scale-110"
            onClick={closeLightbox}
          >
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Navigation Buttons */}
          <button
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all duration-200 hover:scale-110"
            onClick={showPrev}
          >
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all duration-200 hover:scale-110"
            onClick={showNext}
          >
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          {/* Image Container */}
          <div
            className="relative max-w-[95vw] max-h-[90vh] mx-4 md:mx-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative rounded-xl overflow-hidden bg-gradient-to-br from-gray-900 to-black p-2 md:p-4">
              <img
                src={currentImage}
                alt="Expanded view"
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              />

              {/* Image Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      {gallerySections[lightboxState.sectionIndex].title}
                    </h3>
                    <p className="text-gray-300 mt-1">
                      Image {lightboxState.imageIndex + 1} of{" "}
                      {
                        gallerySections[lightboxState.sectionIndex].images
                          .length
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Controls */}
          <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <span className="text-sm text-white">Press ESC to close</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
